import { EMPTY_TILE } from "../document/packed-tile.ts";
import type { EntityInstance, MapDocument, PropInstance, PropLayer, WorldDocument } from "../document/types.ts";
import { propFeet } from "../editor/objects.ts";
import { tieKeys, type OrderedProp } from "../editor/sort.ts";
import type { AppearanceTable } from "./behaviour.ts";
import type { CompileContext } from "./context.ts";
import { COMPILED_COLLISION_CELLS_PER_TILE } from "./collision.ts";
import type { ResolvedMap } from "./resolve-map.ts";
import { foldZones } from "../document/zones.ts";
import { foldDoors } from "../document/doors.ts";
import { checkDoorFields, chunkDoors, type ChunkDoors } from "./doors.ts";
import { chunkSurfaces, mapSurfaces, type ChunkSurfaces } from "./surfaces.ts";
import { chunkLandmarks } from "./landmarks.ts";
import { chunkMarkTable, mapMarks } from "./marks.ts";
import { chunkSourceTable, type ChunkSource } from "./sources.ts";
import { checkZoneTypes, chunkZones, type ChunkZones } from "./zones.ts";
import { chunkStands, type ChunkStand } from "./stand-chunk.ts";
import { mapStandTable } from "./stand-map.ts";
import { chunkSight, mapSight, type ChunkSight } from "./sight.ts";

/**
 * Slices a resolved map into chunks (ADR-016 item 6). Every chunk of the grid is emitted, edge chunks are
 * padded with `EMPTY_TILE`, props belong to the chunk containing `(x, y)`, collision layers are folded into
 * the server grid and never listed, and entities go to the chunk
 * containing `(floor(x), floor(y))`. Layers keep document order, which is render order.
 *
 * A prop carrying a behaviour (ADR-053) leaves every prop layer, since the live thing is drawn from the
 * snapshot rather than twice, and the server chunk gains an entity for it on a layer of its own: its type,
 * its feet point, its fields and the index of its appearance. A prop whose behaviour is driven by presence
 * (ADR-058 amendment, P1.3-T4) is no thing: it stays in its prop layer on both sides, carrying the index of
 * its appearance, and each viewer's client drives it. Every prop a chunk keeps carries its tie key over its
 * whole layer (P1.6-T42), so equal depths draw in one order however a client's chunks reach its views.
 */

/**
 * Version of the compiled chunk format. Version 3 carries a server chunk's `zones` (ADR-054's amendment of
 * 2026-09-27, decision 6), which a reader of 2 would ignore, playing every painted zone as open ground.
 * Version 4 lets a tile cell carry mirror bits (ADR-089), which a reader of 3 would take for a tileset index.
 * Version 5 carries a prop's sort offset, and a thing's in the manifest, in whole pixels as `sortPixels`
 * (ADR-098), where a reader of 4 expects whole rows as `sortOffset` and refuses the chunk by its version.
 * Version 6 lets a server chunk carry `doors` (ADR-122 decision 2.3), which a reader of 5 would ignore, playing
 * every door as open ground; it refuses the chunk by its version instead.
 * Version 7 carries `collision` at the 4 px compiled cell (ADR-113 decision 11), where a reader of 6 indexes it at 8 px
 * and would stand characters in the wrong cells; it refuses the chunk by its version instead.
 * Version 8 lets a client chunk carry `stand` (ADR-132 decision 5), the table of cells where a character is drawn
 * behind or in front of a prop; a reader of 7 would ignore it and draw every character by its row alone, so it
 * refuses the chunk by its version instead.
 */
export const COMPILED_SCHEMA_VERSION = 8;

/**
 * A resolved tile layer's cells inside one chunk, `chunkSize * chunkSize` packed values.
 */
export interface ChunkTileLayer {
  readonly kind: "tile";
  readonly id: string;
  readonly name: string;
  readonly variant: string | null;
  readonly tiles: Uint32Array;
}

/**
 * A prop of a chunk. `appearance` is the index into the world's appearance table carried by a prop whose
 * behaviour is driven by presence (ADR-058 amendment, P1.3-T4), and absent on every other prop. `order` is
 * the prop's tie key over its whole layer (`tieKeys`, P1.6-T42), which every compile writes and a chunk
 * written before it lacks.
 */
export interface ChunkProp extends OrderedProp {
  readonly appearance?: number;
}

/**
 * The props of one prop layer inside one chunk.
 */
export interface ChunkPropLayer {
  readonly kind: "prop";
  readonly id: string;
  readonly name: string;
  readonly props: readonly ChunkProp[];
}

/**
 * An entity of a chunk. `appearance` is the index into the world's appearance table carried by an entity the
 * compiler lowered from a behaviour prop (ADR-053), and absent on an entity the author placed.
 */
export interface ChunkEntity extends EntityInstance {
  readonly appearance?: number;
}

/**
 * The entities of one entity layer inside one chunk.
 */
export interface ChunkEntityLayer {
  readonly kind: "entity";
  readonly id: string;
  readonly name: string;
  readonly entities: readonly ChunkEntity[];
}

export type ChunkLayer = ChunkTileLayer | ChunkPropLayer | ChunkEntityLayer;

/**
 * One thing a client chunk lists for a picture to draw (ADR-100 decision 56): the index into the world's
 * appearance table and the thing's feet point in sixteenths of a tile, the units the snapshot carries and
 * `thingPlace` reads. Server chunks carry none.
 */
export interface ChunkLandmark {
  readonly appearance: number;
  readonly x: number;
  readonly y: number;
}

/**
 * A marked prop of a chunk (`CompileContext.markedFrames`): its authored id, its index `n` among the map's marked props
 * sorted by id (`markedPropOrder`), the mark the game gave its frame, and its feet point in whole sixteenths of a tile.
 * A client chunk lists them so the page knows which of its props carry the game's meaning; a server chunk lists the same
 * so the map server knows where each stands.
 */
export interface ChunkMark {
  readonly id: string;
  readonly n: number;
  readonly mark: string;
  readonly x: number;
  readonly y: number;
}

/**
 * A compiled chunk. Client and server chunks share this shape; they differ in which entities they hold and
 * in `collision`, which both sides carry (`(chunkSize * COMPILED_COLLISION_CELLS_PER_TILE)^2` bytes at the 4 px
 * compiled cell, ADR-113; `zones` and `doors` stay at the authoring cell, ADR-017 as amended by ADR-032 and ADR-040: the client predicts against it; the server stays
 * authoritative). `zones` is the chunk's cells of the map's folded zone grid (`ChunkZones`), carried by a
 * server chunk holding a painted cell and null otherwise; the client chunk always carries null, since zones
 * never reach the page. `doors` is the chunk's cells of the map's folded doors (`ChunkDoors`), carried by a server
 * chunk holding a door cell and null otherwise; the client chunk always carries null, so a page never learns where a
 * door is or where it leads. `surfaces` is the chunk's tiles of the map's ground surfaces (`ChunkSurfaces`), carried by a
 * client chunk holding a tile the game names a surface for and absent otherwise, as on every server chunk and on a
 * chunk compiled before surfaces, which a reader takes as no surface anywhere. `landmarks` (ADR-100 decision 56) is
 * the client chunk's list of the things whose feet fall in it that a map picture draws, in layer then prop order,
 * absent when there is none, on every server chunk and on a chunk compiled before it, which a reader takes as no
 * landmark anywhere. `stand` (ADR-132 decision 5) is the client chunk's table of the cells a builder painted Behind or
 * In front of a prop, with the side and the depth of the prop that decides each (`ChunkStand`); absent when no cell of
 * the chunk is painted, on every server chunk (which reads only what blocks) and on a chunk compiled before it.
 * `sight` (ADR-135 decision 2) is the server chunk's cut of the map's sight planes, carried by a server chunk
 * holding a cell in a room and absent otherwise, on every client chunk and on a chunk compiled before it, which a
 * reader takes as no room anywhere. The format version does not move for it: a reader that does not know the
 * planes ignores them and withholds nothing.
 */
export interface CompiledChunk {
  readonly schemaVersion: number;
  readonly worldId: string;
  readonly mapId: string;
  readonly cx: number;
  readonly cy: number;
  readonly chunkSize: number;
  readonly tilesets: readonly string[];
  readonly collision: Uint8Array | null;
  readonly zones: ChunkZones | null;
  readonly doors: ChunkDoors | null;
  readonly surfaces?: ChunkSurfaces;
  readonly landmarks?: readonly ChunkLandmark[];
  readonly stand?: ChunkStand;
  readonly sight?: ChunkSight;
  readonly marks?: readonly ChunkMark[];
  /** The server chunk's energy sources (ADR-141's amendment of 2026-11-01, decision 6); absent when it has none and on every client chunk. */
  readonly sources?: readonly ChunkSource[];
  readonly layers: readonly ChunkLayer[];
}

/**
 * The client and server chunk at one grid position.
 */
export interface ChunkPair {
  readonly cx: number;
  readonly cy: number;
  readonly client: CompiledChunk;
  readonly server: CompiledChunk;
}

/**
 * Copies one chunk's cells out of a whole-map grid, padding beyond the map with `fill`.
 *
 * @param cells Whole-map cells.
 * @param out Preallocated chunk buffer, `chunkSize * chunkSize` long.
 * @param fill Padding value.
 * @param width Map width.
 * @param height Map height.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns `out`.
 */
export function sliceGrid<T extends Uint8Array | Uint32Array>(cells: T, out: T, fill: number, width: number, height: number, cx: number, cy: number, chunkSize: number): T {
  out.fill(fill);
  const x0 = cx * chunkSize;
  const y0 = cy * chunkSize;
  const columns = Math.min(chunkSize, width - x0);
  for (let y = 0; y < chunkSize && y0 + y < height; y += 1) {
    out.set(cells.subarray((y0 + y) * width + x0, (y0 + y) * width + x0 + columns), y * chunkSize);
  }
  return out;
}

/**
 * Reports whether a cell coordinate falls in a chunk.
 *
 * @param x Cell column.
 * @param y Cell row.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns True when the cell is inside the chunk.
 */
export function inChunk(x: number, y: number, cx: number, cy: number, chunkSize: number): boolean {
  return Math.floor(x / chunkSize) === cx && Math.floor(y / chunkSize) === cy;
}

/**
 * The appearance index of a prop driven by presence (ADR-058 amendment, P1.3-T4): one whose appearance
 * names an inside state.
 *
 * @param prop The placed prop.
 * @param appearances The world's appearance table.
 * @returns The index, or undefined for a prop without behaviour or one the server drives.
 */
export function presenceIndex(prop: PropInstance, appearances: AppearanceTable): number | undefined {
  const index = appearances.indexOf.get(prop);
  return index !== undefined && appearances.appearances[index]?.inside !== undefined ? index : undefined;
}

/**
 * The tie key of every prop of a map's prop layers (P1.6-T42), keyed over each whole layer, behaviour props
 * included, as the editor keys the layer it draws.
 *
 * @param map The map document.
 * @param appearances The world's appearance table, which says which props are driven by presence.
 * @returns Each prop's key.
 */
function mapTieKeys(map: MapDocument, appearances: AppearanceTable): ReadonlyMap<PropInstance, number> {
  const keys = new Map<PropInstance, number>();
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      const layerKeys = tieKeys(layer.props, (prop) => presenceIndex(prop, appearances) !== undefined);
      layer.props.forEach((prop, i) => keys.set(prop, layerKeys[i] as number));
    }
  }
  return keys;
}

/**
 * The props a prop layer keeps inside one chunk, each with its tie key: those without behaviour as they are,
 * and those driven by presence without their behaviour and with the index of their appearance.
 *
 * @param layer The prop layer.
 * @param appearances The world's appearance table.
 * @param keys Each prop's tie key.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns The chunk's props, in layer order.
 */
function keptProps(layer: PropLayer, appearances: AppearanceTable, keys: ReadonlyMap<PropInstance, number>, cx: number, cy: number, chunkSize: number): ChunkProp[] {
  const props: ChunkProp[] = [];
  for (const prop of layer.props) {
    if (!inChunk(prop.x, prop.y, cx, cy, chunkSize)) {
      continue;
    }
    const order = keys.get(prop) as number;
    if (prop.behaviour === undefined) {
      props.push({ ...prop, order });
      continue;
    }
    const appearance = presenceIndex(prop, appearances);
    if (appearance !== undefined) {
      props.push({ id: prop.id, frame: prop.frame, x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY, sortPixels: prop.sortPixels, appearance, order });
    }
  }
  return props;
}

/**
 * The entities a prop layer's behaviour props lower to inside one chunk (ADR-053): each one at the prop's
 * feet point, carrying its fields and the index of its appearance. A prop driven by presence lowers to none.
 *
 * @param layer The prop layer.
 * @param appearances The world's appearance table.
 * @param tileSize Tile size in pixels.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns The layer's lowered entities inside the chunk, in prop order.
 */
function loweredEntities(layer: PropLayer, appearances: AppearanceTable, tileSize: number, cx: number, cy: number, chunkSize: number): ChunkEntity[] {
  const entities: ChunkEntity[] = [];
  for (const prop of layer.props) {
    const appearance = appearances.indexOf.get(prop);
    const behaviour = prop.behaviour;
    if (appearance === undefined || behaviour === undefined || presenceIndex(prop, appearances) !== undefined) {
      continue;
    }
    const feet = loweredFeet(prop, appearances, appearance, tileSize);
    if (inChunk(Math.floor(feet.x), Math.floor(feet.y), cx, cy, chunkSize)) {
      entities.push({ id: prop.id, type: behaviour.type, x: feet.x, y: feet.y, fields: behaviour.fields, appearance });
    }
  }
  return entities;
}

/**
 * The feet point a behaviour prop lowers to: the bottom centre of the frame its initial state shows.
 *
 * @param prop The placed prop.
 * @param appearances The world's appearance table.
 * @param index The prop's appearance index.
 * @param tileSize Tile size in pixels.
 * @returns The feet point in tiles.
 */
export function loweredFeet(prop: PropInstance, appearances: AppearanceTable, index: number, tileSize: number): { readonly x: number; readonly y: number } {
  const appearance = appearances.appearances[index];
  return propFeet(prop, appearance?.states[appearance.initial]?.width ?? 0, tileSize);
}

/**
 * Builds the layers of one chunk.
 *
 * @param map Map document.
 * @param resolved Resolved tile layers.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @param tileSize Tile size in pixels.
 * @param serverOnly Entity types to exclude, or null to keep every entity and lower the behaviour props.
 * @param appearances The world's appearance table.
 * @param keys Each prop's tie key.
 * @returns The chunk's layers in document order.
 */
function chunkLayers(map: MapDocument, resolved: ResolvedMap, cx: number, cy: number, chunkSize: number, tileSize: number, serverOnly: ReadonlySet<string> | null, appearances: AppearanceTable, keys: ReadonlyMap<PropInstance, number>): ChunkLayer[] {
  const layers: ChunkLayer[] = [];
  for (const layer of map.layers) {
    if (layer.kind === "terrain" || layer.kind === "tile") {
      const tiles = resolved.layers.find((r) => r.id === layer.id);
      if (tiles === undefined) {
        throw new Error(`layer ${layer.id} has no resolved tiles`);
      }
      layers.push({ kind: "tile", id: layer.id, name: layer.name, variant: tiles.variant, tiles: sliceGrid(tiles.tiles, new Uint32Array(chunkSize * chunkSize), EMPTY_TILE, map.width, map.height, cx, cy, chunkSize) });
    } else if (layer.kind === "prop") {
      layers.push({ kind: "prop", id: layer.id, name: layer.name, props: keptProps(layer, appearances, keys, cx, cy, chunkSize) });
      if (serverOnly === null) {
        const entities = loweredEntities(layer, appearances, tileSize, cx, cy, chunkSize);
        if (entities.length > 0) {
          layers.push({ kind: "entity", id: `${layer.id}:things`, name: layer.name, entities });
        }
      }
    } else if (layer.kind === "entity") {
      const entities = layer.entities.filter((e) => inChunk(Math.floor(e.x), Math.floor(e.y), cx, cy, chunkSize) && (serverOnly === null || !serverOnly.has(e.type)));
      layers.push({ kind: "entity", id: layer.id, name: layer.name, entities });
    }
  }
  return layers;
}

/**
 * Slices a resolved map into client and server chunks.
 *
 * @param world World document.
 * @param map One of its maps.
 * @param resolved The map's resolved tile layers.
 * @param context Compile context, for entity visibility.
 * @param appearances The world's appearance table, for lowering behaviour props into the server chunk.
 * @param sightNotes Receives what the sight planes leave out (`mapSight`).
 * @returns Chunk pairs in row-major grid order.
 * @throws Error when a resolved layer or the collision grid has the wrong cell count, a tile-bearing layer is
 *   unresolved, an entity has a type the context does not list, a placed entity names an object type that
 *   ADR-053 moved onto props, a prop or entity lies outside the map, or a zone layer paints a type the
 *   context does not register, a door holds a field the game does not register, a chunk meets more doors than a
 *   byte indexes, or the game names more surfaces than a cell's byte holds.
 */
export function chunkMap(world: WorldDocument, map: MapDocument, resolved: ResolvedMap, context: CompileContext, appearances: AppearanceTable, sightNotes: string[] = []): ChunkPair[] {
  for (const layer of resolved.layers) {
    if (layer.tiles.length !== map.width * map.height) {
      throw new Error(`resolved layer ${layer.id} has ${layer.tiles.length} cells, expected ${map.width * map.height} (${map.width}x${map.height})`);
    }
  }
  const cellWidth = map.width * COMPILED_COLLISION_CELLS_PER_TILE;
  const cellHeight = map.height * COMPILED_COLLISION_CELLS_PER_TILE;
  if (resolved.collision.length !== cellWidth * cellHeight) {
    throw new Error(`collision grid has ${resolved.collision.length} cells, expected ${cellWidth * cellHeight} (${cellWidth}x${cellHeight})`);
  }
  const known = new Map(context.entityTypes.map((t) => [t.type, t.serverOnly]));
  const bounds = `${map.width}x${map.height}`;
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        if (prop.x < 0 || prop.y < 0 || prop.x >= map.width || prop.y >= map.height) {
          throw new Error(`prop ${prop.id} at (${prop.x}, ${prop.y}) is outside map ${map.id} (${bounds})`);
        }
        const index = appearances.indexOf.get(prop);
        if (index !== undefined && presenceIndex(prop, appearances) === undefined) {
          const feet = loweredFeet(prop, appearances, index, world.tileSize);
          if (feet.x < 0 || feet.y < 0 || feet.x >= map.width || feet.y >= map.height) {
            throw new Error(`behaviour prop ${prop.id} stands at (${feet.x}, ${feet.y}), outside map ${map.id} (${bounds})`);
          }
        }
      }
    } else if (layer.kind === "entity") {
      for (const entity of layer.entities) {
        if (!known.has(entity.type)) {
          if (context.objectTypes.some((t) => t.type === entity.type)) {
            throw new Error(`entity ${entity.id} has type ${entity.type}, which is now a behaviour a prop carries (ADR-053); run bun run world:migrate ${world.id}`);
          }
          throw new Error(`entity ${entity.id} has type ${entity.type}, which the game did not register`);
        }
        if (entity.x < 0 || entity.y < 0 || entity.x >= map.width || entity.y >= map.height) {
          throw new Error(`entity ${entity.id} at (${entity.x}, ${entity.y}) is outside map ${map.id} (${bounds})`);
        }
      }
    }
  }
  checkZoneTypes(map, context);
  checkDoorFields(map, context);
  const folded = foldZones(map);
  const doors = foldDoors(map);
  const ground = mapSurfaces(world, map, context.surfaces, context);
  const serverOnly = new Set([...known].filter(([, s]) => s).map(([type]) => type));
  const tilesets = world.tilesets.map((t) => t.id);
  const chunkSize = world.chunkSize;
  const columns = Math.ceil(map.width / chunkSize);
  const rows = Math.ceil(map.height / chunkSize);
  const keys = mapTieKeys(map, appearances);
  const sight = mapSight(world, map, context, appearances, sightNotes);
  const stands = chunkStands(mapStandTable(map, context, appearances, keys, world.tileSize), chunkSize);
  const marked = chunkMarkTable(mapMarks(map, context, world.tileSize), chunkSize, columns);
  const powered = chunkSourceTable(map, context, world.tileSize, chunkSize, columns);
  const chunks: ChunkPair[] = [];
  for (let cy = 0; cy < rows; cy += 1) {
    for (let cx = 0; cx < columns; cx += 1) {
      const header = { schemaVersion: COMPILED_SCHEMA_VERSION, worldId: world.id, mapId: map.id, cx, cy, chunkSize, tilesets };
      const collisionSize = chunkSize * COMPILED_COLLISION_CELLS_PER_TILE;
      const collision = sliceGrid(resolved.collision, new Uint8Array(collisionSize * collisionSize), context.collision.outside, cellWidth, cellHeight, cx, cy, collisionSize);
      const surfaces = chunkSurfaces(ground, map, cx, cy, chunkSize);
      const landmarks = chunkLandmarks(map, context, appearances, world.tileSize, cx, cy, chunkSize);
      const stand = stands.get(cy * columns + cx);
      const roomSight = sight === null ? null : chunkSight(sight, map, cx, cy, chunkSize);
      const marks = marked.get(cy * columns + cx);
      const sources = powered.get(cy * columns + cx);
      chunks.push({
        cx,
        cy,
        client: { ...header, collision, zones: null, doors: null, ...(surfaces === null ? {} : { surfaces }), ...(landmarks.length === 0 ? {} : { landmarks }), ...(stand === undefined ? {} : { stand }), ...(marks === undefined ? {} : { marks }), layers: chunkLayers(map, resolved, cx, cy, chunkSize, world.tileSize, serverOnly, appearances, keys) },
        server: { ...header, collision, zones: chunkZones(folded, map, cx, cy, chunkSize), doors: chunkDoors(doors, map, { cx, cy, size: chunkSize }, context.doorFields), ...(roomSight === null ? {} : { sight: roomSight }), ...(marks === undefined ? {} : { marks }), ...(sources === undefined ? {} : { sources }), layers: chunkLayers(map, resolved, cx, cy, chunkSize, world.tileSize, null, appearances, keys) },
      });
    }
  }
  return chunks;
}
